import type { ID } from "@/lib/catalog/types";
import { api } from "@/lib/apiClient";
import { isAxiosError } from "axios";

export interface PurchaseCostTypeRecord {
  id: ID;
  /** Chart of accounts number */
  code: string;
  label: string;
  purchaseCostCategoryId: string;
  categoryName: string;
  chartOfAccountsId: string | null;
  createdAt: string;
}

const LS_KEY = "inventory.purchaseCostTypes.v3";

type PurchaseCostTypeApiRecord = {
  id?: number | string;
  code?: string | null;
  label?: string | null;
  name?: string | null;
  number?: string | number | null;
  purchase_cost_category_id?: number | string | null;
  account_detail_type_id?: number | string | null;
  account_detail_type_title?: string | null;
  category?: { id?: number | string; name?: string } | null;
  chart_of_accounts_id?: number | string | null;
  created_at?: string | null;
};

type UpsertPurchaseCostTypeApiResponse = {
  message?: string;
  data?: PurchaseCostTypeApiRecord;
};

type DeletePurchaseCostTypeApiResponse = {
  message?: string;
};

type UpsertPurchaseCostTypeResult = {
  record: PurchaseCostTypeRecord;
  message: string;
};

type DeletePurchaseCostTypeResult = {
  success: boolean;
  message: string;
};

function dedupeById(items: PurchaseCostTypeRecord[]): PurchaseCostTypeRecord[] {
  const byId = new Map<ID, PurchaseCostTypeRecord>();
  for (const item of items) byId.set(item.id, item);
  return Array.from(byId.values());
}

function load(): PurchaseCostTypeRecord[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as PurchaseCostTypeRecord[];
    return Array.isArray(parsed) ? dedupeById(parsed) : [];
  } catch {
    return [];
  }
}

function save(items: PurchaseCostTypeRecord[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(dedupeById(items)));
}

function extractRowsFromResponse(body: unknown): PurchaseCostTypeApiRecord[] {
  if (Array.isArray(body)) return body as PurchaseCostTypeApiRecord[];
  if (!body || typeof body !== "object") return [];
  const root = body as Record<string, unknown>;
  const data = root.data;
  if (Array.isArray(data)) return data as PurchaseCostTypeApiRecord[];
  if (data && typeof data === "object" && !Array.isArray(data)) {
    const inner = (data as Record<string, unknown>).data;
    if (Array.isArray(inner)) return inner as PurchaseCostTypeApiRecord[];
  }
  return [];
}

function readLastPageFromListBody(body: unknown): number | null {
  if (!body || typeof body !== "object") return null;
  const meta = (body as Record<string, unknown>).meta;
  if (meta && typeof meta === "object" && !Array.isArray(meta)) {
    const lp = (meta as Record<string, unknown>).last_page;
    if (typeof lp === "number" && lp >= 1) return lp;
  }
  return null;
}

function extractFromUpsertBody(body: unknown): PurchaseCostTypeApiRecord | null {
  if (!body || typeof body !== "object") return null;
  const data = (body as Record<string, unknown>).data;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    return data as PurchaseCostTypeApiRecord;
  }
  return null;
}

function mapApiRecord(record: PurchaseCostTypeApiRecord): PurchaseCostTypeRecord {
  const categoryId = record.purchase_cost_category_id ?? record.account_detail_type_id ?? record.category?.id ?? "";
  const coaId = record.chart_of_accounts_id ?? record.id;
  const number = record.number != null ? String(record.number) : record.code ?? "";
  return {
    id: String(record.id ?? ""),
    code: number,
    label: record.label || record.name || "",
    purchaseCostCategoryId: categoryId === null || categoryId === "" ? "" : String(categoryId),
    categoryName: record.category?.name ?? record.account_detail_type_title ?? "",
    chartOfAccountsId: coaId == null || coaId === "" ? null : String(coaId),
    createdAt: record.created_at || new Date().toISOString(),
  };
}

export function listPurchaseCostTypeRecords(): PurchaseCostTypeRecord[] {
  return load().sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }) || a.label.localeCompare(b.label));
}

export function getPurchaseCostTypeById(id: ID): PurchaseCostTypeRecord | undefined {
  return load().find((t) => t.id === id);
}

const PER_PAGE = 50;

export async function syncPurchaseCostTypesFromApi() {
  try {
    const collected: PurchaseCostTypeApiRecord[] = [];
    let page = 1;
    let lastPage = 1;
    do {
      const response = await api.get("/api/v1/catalog/purchase-cost-types", {
        params: { page, per_page: PER_PAGE },
      });
      const body = response.data;
      collected.push(...extractRowsFromResponse(body));
      lastPage = readLastPageFromListBody(body) ?? 1;
      page += 1;
    } while (page <= lastPage && page <= 50);

    const records = dedupeById(collected.map(mapApiRecord).filter((r) => Boolean(r.id)));
    save(records);
    return listPurchaseCostTypeRecords();
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 403) {
      throw error;
    }
    console.error("[PurchaseCostTypes] sync failed", error);
    return listPurchaseCostTypeRecords();
  }
}

export async function createPurchaseCostType(data: {
  label: string;
  purchaseCostCategoryId: string;
}): Promise<UpsertPurchaseCostTypeResult> {
  const items = load();
  const payload = {
    name: data.label.trim(),
    purchase_cost_category_id: Number(data.purchaseCostCategoryId) || data.purchaseCostCategoryId,
  };
  const response = await api.post<UpsertPurchaseCostTypeApiResponse>("/api/v1/catalog/purchase-cost-types", payload);
  const created = extractFromUpsertBody(response.data);
  if (!created || created.id == null) {
    throw new Error("Invalid create purchase cost type response.");
  }
  const record = mapApiRecord(created);
  const existingIndex = items.findIndex((item) => item.id === record.id);
  if (existingIndex >= 0) {
    items[existingIndex] = record;
  } else {
    items.unshift(record);
  }
  save(items);
  return {
    record,
    message: response.data?.message || "Purchase cost type created successfully.",
  };
}

export async function updatePurchaseCostType(
  id: ID,
  data: { label: string; purchaseCostCategoryId: string },
): Promise<UpsertPurchaseCostTypeResult | null> {
  const items = load();
  const i = items.findIndex((t) => t.id === id);
  if (i === -1) return null;
  const payload = {
    name: data.label.trim(),
    purchase_cost_category_id: Number(data.purchaseCostCategoryId) || data.purchaseCostCategoryId,
  };
  const response = await api.put<UpsertPurchaseCostTypeApiResponse>(`/api/v1/catalog/purchase-cost-types/${id}`, payload);
  const updated = extractFromUpsertBody(response.data);
  if (!updated) {
    throw new Error("Invalid update purchase cost type response.");
  }
  items[i] = mapApiRecord(updated);
  save(items);
  return {
    record: items[i],
    message: response.data?.message || "Purchase cost type updated successfully.",
  };
}

export async function deletePurchaseCostType(id: ID): Promise<DeletePurchaseCostTypeResult> {
  const response = await api.delete<DeletePurchaseCostTypeApiResponse>(`/api/v1/catalog/purchase-cost-types/${id}`);
  const items = load();
  const nextItems = items.filter((t) => t.id !== id);
  const deleted = nextItems.length !== items.length;
  if (deleted) {
    save(nextItems);
  }
  return {
    success: deleted,
    message: response.data?.message || "Purchase cost type deleted successfully.",
  };
}

export function replaceAllPurchaseCostTypes(records: PurchaseCostTypeRecord[]) {
  save(records);
}
